import React from "react";
import { Avatar, Badge, Box, SxProps, Typography } from "@mui/material";
import { RouterLink } from "./RouterLink";
import { useTranslation } from "react-i18next";

interface IStyleProps {
  flexibleWidth?: boolean;
}

interface IAvatarCardProps extends IStyleProps {
  imageUrl: string | null;
  primaryText: string;
  secondaryText?: string | null;
  secondaryTextColor?: "error";
  blurText?: boolean;
  badge?: JSX.Element;
  avatarIcon?: JSX.Element;
  hideAvatar?: boolean;
  small?: boolean;
  whiteText?: boolean;
  action?: React.ReactNode;
  linkUrl?: string;
  sx?: SxProps;
  avatarSx?: SxProps;
}

export const AvatarCard: React.FC<IAvatarCardProps> = ({
  imageUrl,
  primaryText,
  secondaryText,
  secondaryTextColor,
  flexibleWidth,
  blurText,
  badge,
  avatarIcon,
  hideAvatar,
  small,
  whiteText,
  action,
  linkUrl,
  sx,
  avatarSx,
}) => {
  const { t: translate } = useTranslation("common");
  const badgeView = (
    <Badge
      sx={{
        mr: (theme) => theme.spacing(1),
        ...(small && {
          "& .MuiBadge-badge": {
            position: "absolute",
            top: 14,
            right: 10,
          },
        }),
      }}
      badgeContent={avatarIcon}
      overlap="circular"
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
    >
      <Avatar
        aria-label={translate("avatar_card.avatar_label", {
          name: primaryText,
        })}
        sx={{
          width: (theme) => theme.spacing(5),
          ...(small && {
            width: 24,
            height: 24,
            fontSize: 14,
          }),
          ...avatarSx,
        }}
        src={imageUrl || ""}
      >
        {primaryText[0]}
      </Avatar>
    </Badge>
  );

  const textView = (
    <Typography
      title={primaryText}
      variant={small ? "subtitle2" : "body1"}
      color={whiteText ? "common.white" : "textPrimary"}
      sx={{
        textShadow: blurText ? "0 0 8px black" : undefined,
        color: blurText
          ? "transparent"
          : whiteText
            ? "common.white"
            : "background.dark",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: flexibleWidth ? "auto" : 180,
        display: "block",
        textAlign: "left",
        fontSize: 14,
      }}
    >
      {primaryText}
    </Typography>
  );
  const secondaryTextView = (
    <Typography
      variant="caption"
      color={
        blurText
          ? "transparent"
          : whiteText
            ? "common.white"
            : secondaryTextColor
              ? secondaryTextColor
              : "background.dark"
      }
      title={secondaryText || ""}
      sx={{
        textShadow: blurText ? "0 0 8px black" : undefined,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "block",
        textAlign: "left",
      }}
    >
      {secondaryText}
    </Typography>
  );
  return (
    <Box
      sx={{
        width: flexibleWidth ? "auto" : 228,
        display: "flex",
        alignItems: "center",
        ...sx,
      }}
    >
      {!hideAvatar && linkUrl ? (
        <RouterLink sx={{ textDecoration: "none" }} to={linkUrl}>
          {badgeView}
        </RouterLink>
      ) : (
        !hideAvatar && badgeView
      )}
      <Box sx={{ width: flexibleWidth ? "auto" : 180 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          {linkUrl ? (
            <RouterLink sx={{ textDecoration: "none" }} to={linkUrl}>
              {textView}
            </RouterLink>
          ) : (
            textView
          )}
          {action}
          {badge && badge}
        </Box>

        {!small && linkUrl ? (
          <RouterLink sx={{ textDecoration: "none" }} to={linkUrl}>
            {secondaryTextView}
          </RouterLink>
        ) : (
          !small && secondaryTextView
        )}
      </Box>
    </Box>
  );
};
